import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import ActivityTimeline from '../shared/ActivityTimeline';

const TaskActivityTimeline = ({ taskId, referralId, isExpanded, onToggle, refreshTrigger, onViewCall, isAnkrAdmin = false }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activityCount, setActivityCount] = useState(null);

  const fetchTaskActivities = async (forceRefresh = false) => {
    if (!forceRefresh && activities.length > 0) return; // Already loaded

    setLoading(true);
    try {
      const response = await adminApi.get(`admin/referrals/${referralId}/activities/`, {
        params: { task_id: taskId }
      });

      if (response.data?.results) {
        setActivities(response.data.results);
        setActivityCount(response.data.count || response.data.results.length);
      }
    } catch (error) {
      console.error('Failed to fetch task activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isExpanded && activities.length === 0) {
      fetchTaskActivities();
    }
    if (onToggle) {
      onToggle(taskId);
    }
  };

  const handleRefresh = () => {
    fetchTaskActivities(true); // Force refresh without clearing activities to prevent ActivityTimeline unmount
  };

  const handleFetchActivityWithPayload = async (activityId) => {
    const response = await adminApi.get(`admin/referrals/${referralId}/activities/${activityId}/`);
    return response.data;
  };

  // Fetch data when expanded (handles tab switching)
  useEffect(() => {
    if (isExpanded && activities.length === 0) {
      fetchTaskActivities();
    }
  }, [isExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch for refreshTrigger changes and auto-refresh if expanded
  useEffect(() => {
    if (refreshTrigger > 0 && isExpanded) {
      handleRefresh();
    }
  }, [refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mt-3">
      <div
        className="d-flex justify-content-between align-items-center cursor-pointer py-2 border-top"
        onClick={handleToggle}
        style={{ cursor: 'pointer' }}
      >
        <div className="d-flex align-items-center">
          <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} me-2 text-muted`}></i>
          <small className="text-muted fw-bold">Task Activity</small>
          {activityCount !== null && (
            <span className="badge bg-secondary ms-2" style={{ fontSize: '0.7em' }}>
              {activityCount}
            </span>
          )}
        </div>
        {loading && (
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="mt-2 border rounded p-2 bg-light">
          <ActivityTimeline
            activities={activities}
            loading={loading}
            onRefresh={handleRefresh}
            onViewCall={onViewCall}
            onFetchActivityWithPayload={handleFetchActivityWithPayload}
            showAutoRefresh={true}
            autoRefreshInterval={5000}
            title=""
            emptyMessage="No activities for this task yet"
            isAnkrAdmin={isAnkrAdmin}
          />
        </div>
      )}
    </div>
  );
};

export default TaskActivityTimeline;
